import React from 'react'
import { DollarSign } from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────

export interface ContractDetail {
  id: number
  contract_number: string
  title: string
  description: string
  contract_value: string | null
  total_outlays: string | null
  amount: string | null
  base_exercised_options: string | null
  base_and_all_options: string | null
  total_account_obligation: string | null
  total_account_outlay: string | null
  awarding_agency: string | null
  awarding_office: string | null
  awarding_agency_code: string | null
  awarding_sub_agency_code: string | null
  funding_agency_name: string | null
  funding_agency_code: string | null
  funding_sub_agency_name: string | null
  funding_office: string | null
  award_date: string | null
  start_date: string | null
  end_date: string | null
  period_of_performance_current_end_date: string | null
  contract_type: string | null
  type_description: string | null
  source_url: string | null
  generated_unique_award_id?: string | null
  product_name?: string | null
  performance_notes?: string | null
}

// ── Helpers ───────────────────────────────────────────────────────

export const fmt = (val: string | number | null | undefined): string => {
  if (val == null || val === '') return '$0.00'
  const n = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(n)) return '$0.00'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)
}

export const fmtShort = (val: string | number | null | undefined): string => {
  if (val == null || val === '') return '$0'
  const n = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(n)) return '$0'
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)} Billion`
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)} Million`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return fmt(n)
}

export const num = (val: string | number | null | undefined): number => {
  if (val == null) return 0
  const n = typeof val === 'string' ? parseFloat(val) : val
  return isNaN(n) ? 0 : n
}

export const fmtDate = (val: string | null | undefined): string => {
  if (!val) return 'N/A'
  return new Date(val).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Build a USAspending.gov award URL from contract data.
 * Prefers source_url if already stored, otherwise constructs from generated_unique_award_id.
 */
export const usaspendingUrl = (contract: ContractDetail): string | null => {
  if (contract.source_url) return contract.source_url
  if (contract.generated_unique_award_id) {
    return `https://www.usaspending.gov/award/${contract.generated_unique_award_id}`
  }
  return null
}

// ── Award Amounts Chart ───────────────────────────────────────────

const AwardAmountsChart: React.FC<{ contract: ContractDetail }> = ({ contract }) => {
  const outlayed = num(contract.total_outlays || contract.total_account_outlay)
  const obligated = num(contract.contract_value || contract.total_account_obligation)
  const currentAward = num(contract.base_exercised_options)
  const potentialAward = num(contract.base_and_all_options)

  const maxVal = Math.max(outlayed, obligated, currentAward, potentialAward, 1)

  const barData = [
    { label: 'Outlayed Amount', value: outlayed, color: 'bg-blue-900 dark:bg-blue-900' },
    { label: 'Obligated Amount', value: obligated, color: 'bg-blue-600 dark:bg-blue-600' },
    { label: 'Current Award Amount', value: currentAward, color: 'bg-blue-400 dark:bg-blue-400' },
    { label: 'Potential Award Amount', value: potentialAward, color: 'bg-blue-200 dark:bg-blue-200/30' },
  ]

  if (potentialAward === 0 && currentAward === 0 && obligated === 0) return null

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <DollarSign className="w-4 h-4" />
        Award Amounts
      </h4>

      {/* Stacked horizontal bar */}
      <div className="relative h-8 bg-muted rounded-lg overflow-hidden">
        {potentialAward > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-blue-200/30 dark:bg-blue-200/10"
            style={{ width: '100%' }}
          />
        )}
        {currentAward > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-blue-400/40 dark:bg-blue-400/25"
            style={{ width: `${(currentAward / maxVal) * 100}%` }}
          />
        )}
        {obligated > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-blue-600/60 dark:bg-blue-600/40"
            style={{ width: `${(obligated / maxVal) * 100}%` }}
          />
        )}
        {outlayed > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-blue-900 dark:bg-blue-800"
            style={{ width: `${(outlayed / maxVal) * 100}%` }}
          />
        )}

        {/* Labels on bar */}
        {obligated > 0 && (
          <div
            className="absolute top-0 flex flex-col justify-center h-full pl-2 text-white text-[10px] font-bold"
            style={{ left: `${Math.min((obligated / maxVal) * 100, 50)}%` }}
          >
            {fmtShort(obligated)}
          </div>
        )}
        {potentialAward > 0 && (
          <div
            className="absolute top-0 right-2 flex flex-col justify-center h-full text-muted-foreground text-[10px] font-semibold"
          >
            {fmtShort(potentialAward)}
          </div>
        )}
      </div>

      {/* Legend table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <tbody>
            {barData.map((row) => (
              <tr key={row.label} className="border-b border-border last:border-0">
                <td className="px-3 py-1.5 flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${row.color} inline-block`} />
                  <span className="text-muted-foreground">{row.label}</span>
                </td>
                <td className="px-3 py-1.5 text-right font-semibold text-foreground">
                  {fmt(row.value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Utilization percentage */}
      {potentialAward > 0 && currentAward > 0 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Contract utilization</span>
          <span className="font-semibold text-foreground">
            {((currentAward / potentialAward) * 100).toFixed(1)}% utilized
            <span className="text-muted-foreground ml-1">
              ({fmtShort(potentialAward - currentAward)} remaining)
            </span>
          </span>
        </div>
      )}
    </div>
  )
}

export default AwardAmountsChart
